// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import cancelOrder from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/CancelOrder";
import reorder from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/Reorder";
import { canCancelOrder, canRmaOrder } from "@insite/client-framework/Store/Pages/OrderDetails/OrderDetailsSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import ShareEntityButton, { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { HiddenProps } from "@insite/mobius/Hidden";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AddToCartButton from "@steiner/App/Components/AddToCartButton/AddToCartButton";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    buttonsOrder = "buttonsOrder",
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
    // xng-change - STR-2619
    showReorderOnly = "showReorderOnly",
}

const enum buttons {
    cancel = "Cancel",
    print = "Print",
    email = "Email",
    reorder = "Reorder",
    rma = "Rma",
    addToList = "addToList",
}

interface ButtonMapper {
    [key: string]: {
        button: any;
        clickable: any;
        showButtonOnTablet?: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.buttonsOrder]: ButtonModel[];
        [fields.generateEmailAttachmentFromWebpage]?: boolean;
        // xng-change - STR-2619
        [fields.showReorderOnly]?: boolean;
    };
}

interface ButtonModel {
    fields: {
        name: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const settingsCollection = getSettingsCollection(state);
    return {
        rmaLink: getPageLinkByPageType(state, "RequestRmaPage"),
        canReorderItems: settingsCollection.orderSettings.canReorderItems,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        isReordering: state.pages.orderDetails.isReordering,
        isUpdatingCart: state.context.isUpdatingCart,
        isVmiOrderDetailsPage: state.pages.orderDetails.isVmiOrderDetailsPage,
        // XNG-Changes
        allowMultipleWishLists: settingsCollection.wishListSettings.allowMultipleWishLists,
    };
};

const mapDispatchToProps = {
    cancelOrder,
    reorder,
    // XNG-Changes
    addToWishList,
    setAddToListModalIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OrderDetailsButtonSetStyles {
    buttonsWrapper?: InjectableCss;
    buttonHidden?: HiddenProps;
    buttonGridContainer?: GridContainerProps;
    tabletButtonHidden?: HiddenProps;
    tabletMenuHidden?: HiddenProps;
    menuHidden?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    cancelButton?: ButtonPresentationProps;
    cancelClickable?: ClickablePresentationProps;
    printButton?: ButtonPresentationProps;
    printClickable?: ClickablePresentationProps;
    reorderButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
    reorderClickable?: ClickablePresentationProps;
    reorderClickableSpinner?: LoadingSpinnerProps;
    rmaButton?: ButtonPresentationProps;
    rmaClickable?: ClickablePresentationProps;
    buttonWrapper?: InjectableCss;
    twoButtonModalStyles?: TwoButtonModalStyles;
    // Xng-Changes
    toasterMessage?: InjectableCss;
    addAllProductGroupButton?: ButtonPresentationProps;
    addAllProductGroupClickable?: ClickablePresentationProps;
    reorderingButton?: ButtonPresentationProps;
}

export const orderDetailsButtonSetStyles: OrderDetailsButtonSetStyles = {
    buttonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            clear: both;
        `,
    },
    shareEntityButtonStyles: {
        button: {
            variant: "secondary",
        },
    },
    buttonHidden: {
        below: "lg",
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
        `,
    },
    tabletButtonHidden: {
        below: "md",
        above: "md",
    },
    tabletMenuHidden: {
        below: "md",
        above: "md",
    },
    menuHidden: {
        above: "sm",
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
        `,
    },
    buttonWrapper: {
        css: css`
            margin-left: 20px;
        `,
    },
    cancelButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    reorderButton: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    rmaButton: {
        buttonType: "solid",
        variant: "tertiary",
    },
    spinner: {
        size: 22,
        css: css`
            margin: 8px 50px 0 34px;
        `,
    },
    reorderClickableSpinner: {
        size: 22,
        css: css`
            margin: 10px 0 5px 15px;
        `,
    },
    twoButtonModalStyles: {
        submitButton: {
            color: "primary",
        },
    },
};

/**
 * @deprecated Use orderDetailsButtonSetStyles instead.
 */
export const emailButtonStyles = orderDetailsButtonSetStyles;
const styles = orderDetailsButtonSetStyles;

const OrderDetailsButtonSet = ({
    cancelOrder,
    reorder,
    fields,
    rmaLink,
    canReorderItems,
    showAddToCartConfirmationDialog,
    isReordering,
    isUpdatingCart,
    history,
    isVmiOrderDetailsPage,
    // XNG-Changes
    allowMultipleWishLists,
    addToWishList,
    setAddToListModalIsOpen,
}: Props) => {
    const { value: order } = useContext(OrderStateContext);
    const canCancel = useAppSelector(state => canCancelOrder(state, order));
    const allowRma = useAppSelector(state => canRmaOrder(state, order));
    const toasterContext = useContext(ToasterContext);
    const [cancelOrderModalIsOpen, setCancelOrderModalIsOpen] = useState(false);
    const resolvedGenerateEmailAttachmentFromWebpage =
        fields.generateEmailAttachmentFromWebpage === true || fields.generateEmailAttachmentFromWebpage === undefined;
    if (!order) {
        return null;
    }

    const canAddOrderToList = order.orderLines?.some(line => line.canAddToWishlist);

    const onClickCancel = () => {
        setCancelOrderModalIsOpen(true);
    };

    const handleModalNoButtonClick = () => {
        setCancelOrderModalIsOpen(false);
    };

    const handleModalYesButtonClick = () => {
        setCancelOrderModalIsOpen(false);
        cancelOrder({ orderNumber: order.webOrderNumber ?? order.erpOrderNumber });
    };

    const onClickPrint = () => {
        if (!order) {
            return;
        }
        openPrintDialog();
    };

    const onClickReorder = () => {
        reorder({
            onSuccess: () => {
                if (!showAddToCartConfirmationDialog) {
                    return;
                }
                toasterContext.addToast({
                    children: (
                        // Xng-Changes
                        <StyledWrapper {...styles.toasterMessage}>
                            <OrderDetailPageTypeLink
                                // Xng-Changes
                                title={order.webOrderNumber || order.erpOrderNumber}
                                orderNumber={order.webOrderNumber || order.erpOrderNumber}
                                isVmiOrderDetailsPage={isVmiOrderDetailsPage}
                            />
                            &nbsp;{translate("Added to Cart")}
                        </StyledWrapper>
                    ),
                    messageType: "success",
                    timeoutLength: 6000,
                });
            },
        });
    };

    const onClickRma = (url: string) => {
        history.push(`${url}?orderNumber=${order.webOrderNumber || order.erpOrderNumber}`);
    };
    const addAllToListClickHandler = () => {
        const productInfos =
            order.orderLines
                ?.filter(o => o.productId && o.qtyOrdered)
                .map(orderLine => ({
                    productId: orderLine.productId!,
                    qtyOrdered: orderLine.qtyOrdered!,
                    unitOfMeasure: orderLine.unitOfMeasure,
                })) || [];
        if (!allowMultipleWishLists) {
            addToWishList({
                productInfos,
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos });
    };
    const buttonList: ButtonMapper = {};
    if (order.canAddToCart && canCancel) {
        buttonList[buttons.cancel] = {
            button: (
                <Button
                    {...styles.cancelButton}
                    onClick={onClickCancel}
                    // XNG-Changes
                    aria-label={translate("Cancel")}
                    data-test-selector="orderDetails_cancelButton"
                >
                    {translate("Cancel")}
                </Button>
            ),
            clickable: (
                <Clickable
                    {...styles.cancelClickable}
                    onClick={onClickCancel}
                    // XNG-Changes
                    aria-label={translate("Cancel")}
                >
                    {translate("Cancel")}
                </Clickable>
            ),
        };
    }
    // xng-change - STR-2619
    // if (order.canAddToCart && canReorderItems) {
    //     buttonList[buttons.reorder] = {
    //         button: (
    //             <AddToCartButton
    //                 onClick={onClickReorder}
    //                 disabled={isUpdatingCart || isReordering}
    //                 data-test-selector="orderDetails_reorderButton"
    //                 // XNG-Changes
    //                 aria-label={translate("Reorder")}
    //                 label={translate(isReordering ? "Reordering" : "Reorder")}
    //             >
    //             </AddToCartButton>
    //         ),
    //         clickable: (
    //             <>
    //                 {!isReordering && (
    //                     <Clickable
    //                         {...styles.reorderClickable}
    //                         onClick={onClickReorder}
    //                         disabled={isUpdatingCart}
    //                         // XNG-Changes
    //                         aria-label={translate("Reorder")}
    //                     >
    //                         {translate("Reorder")}
    //                     </Clickable>
    //                 )}
    //                 {isReordering && <LoadingSpinner {...styles.reorderClickableSpinner} />}
    //             </>
    //         ),
    //         showButtonOnTablet: true,
    //     };
    // }

    if (allowRma && rmaLink) {
        buttonList[buttons.rma] = {
            button: (
                <Button
                    {...styles.rmaButton}
                    onClick={() => onClickRma(rmaLink.url)}
                    data-test-selector="orderDetails_returnRequestButton"
                    // XNG-Changes
                    aria-label={translate("Return Request")}
                >
                    {translate("Return Request")}
                </Button>
            ),
            clickable: (
                <Clickable
                    {...styles.rmaClickable}
                    onClick={() => onClickRma(rmaLink.url)}
                    // XNG-Changes
                    aria-label={translate("Return Request")}
                >
                    {translate("Return Request")}
                </Clickable>
            ),
        };
    }
    // XNG-Changes
    if (canAddOrderToList) {
        buttonList[buttons.addToList] = {
            button: (
                <Button
                    {...styles.addAllProductGroupButton}
                    onClick={addAllToListClickHandler}
                    data-test-selector="orderDetails_AddAllToList"
                    aria-label={translate("Add All to Product Group")}
                >
                    {translate("Add All to Product Group")}
                </Button>
            ),
            clickable: (
                <Clickable
                    {...styles.addAllProductGroupClickable}
                    onClick={addAllToListClickHandler}
                    aria-label={translate("Add All to Product Group")}
                >
                    {translate("Add All to Product Group")}
                </Clickable>
            ),
        };
    }
    buttonList[buttons.email] = {
        button: (
            <ShareEntityButton
                // XNG-Changes
                entityId={order.webOrderNumber || order.erpOrderNumber}
                entityName="Order"
                extendedStyles={styles.shareEntityButtonStyles}
                generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
            />
        ),
        clickable: (
            <ShareEntityButton
                // XNG-Changes
                entityId={order.webOrderNumber || order.erpOrderNumber}
                entityName="Order"
                variant="clickable"
                extendedStyles={styles.shareEntityButtonStyles}
                generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
            />
        ),
    };
    buttonList[buttons.print] = {
        button: (
            <Button {...styles.printButton} onClick={onClickPrint} aria-label="Print">
                {translate("Print")}
            </Button>
        ),
        clickable: (
            <Clickable {...styles.printClickable} onClick={onClickPrint} aria-label="Print">
                {translate("Print")}
            </Clickable>
        ),
    };

    // xng-change - STR-2619
    if (fields.showReorderOnly && order.canAddToCart && canReorderItems) {
        return (
            <AddToCartButton
                onClick={onClickReorder}
                disabled={isUpdatingCart || isReordering}
                data-test-selector="orderDetails_reorderButton"
                // XNG-Changes
                aria-label={translate("Reorder")}
                label={translate(isReordering ? "Reordering" : "Reorder")}
                extendedStyles={styles.reorderButton}
            ></AddToCartButton>
        );
    }

    const buttonsToRender = fields.buttonsOrder.map(button => buttonList[button.fields.name]).filter(button => button);
    // XNG-Changes: change structure
    return (
        <StyledWrapper {...styles.buttonsWrapper}>
            {/* <Hidden {...styles.buttonHidden} data-test-selector="orderDetails_standardButtons"> */}
            {buttonsToRender.map((button, index) => (
                <StyledWrapper {...styles.buttonWrapper} key={index}>
                    {button.button}
                </StyledWrapper>
            ))}
            {/* </Hidden> */}
            {/* <Hidden {...styles.tabletButtonHidden}>
                {buttonsToRender
                    .filter(o => o.showButtonOnTablet)
                    .map((button, index) => (
                        <StyledWrapper {...styles.buttonWrapper} key={index}>
                            {button.button}
                        </StyledWrapper>
                    ))}
            </Hidden>
            <Hidden {...styles.tabletMenuHidden}>
                <OverflowMenu hasChildPortal position="end" {...styles.overflowMenu}>
                    {buttonsToRender
                        .filter(o => !o.showButtonOnTablet)
                        .map((button, index) => (
                            <div key={index}>{button.clickable}</div>
                        ))}
                </OverflowMenu>
            </Hidden>
            <Hidden {...styles.menuHidden}>
                <OverflowMenu hasChildPortal position="end" {...styles.overflowMenu}>
                    {buttonsToRender.map((button, index) => (
                        <div key={index}>{button.clickable}</div>
                    ))}
                </OverflowMenu>
            </Hidden> */}
            <TwoButtonModal
                modalIsOpen={cancelOrderModalIsOpen}
                headlineText={translate("Cancel Order")}
                messageText={siteMessage("Order_ConfirmCancelOrder")}
                cancelButtonText={translate("No")}
                submitButtonText={translate("Yes, Cancel Order")}
                extendedStyles={styles.twoButtonModalStyles}
                onCancel={handleModalNoButtonClick}
                onSubmit={handleModalYesButtonClick}
                submitTestSelector="submitButton"
                cancelTestSelector="cancelButton"
            ></TwoButtonModal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderDetailsButtonSet)),
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
        displayName: "Order Details Button Set",
        group: "Order Details",
        fieldDefinitions: [
            {
                name: fields.buttonsOrder,
                displayName: "Buttons Order",
                editorTemplate: "ListField",
                defaultValue: [
                    { fields: { name: buttons.rma } },
                    { fields: { name: buttons.print } },
                    { fields: { name: buttons.email } },
                    { fields: { name: buttons.cancel } },
                    { fields: { name: buttons.reorder } },
                    { fields: { name: buttons.addToList } },
                ],
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                hideAdd: true,
                hideEdit: true,
                hideDelete: true,
                fieldType: "General",
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: "name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the order generates an email attachment using the Order Details page, rather than a pre-defined PDF file.",
            },
            // xng-change - STR-2619
            {
                name: fields.showReorderOnly,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
        ],
    },
};

export default widgetModule;
